import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import useSiteText from "../hooks/useSiteText";
import { Mail, MapPin, Factory, Clock, Phone } from "lucide-react";
import Picture from "./Picture";
import logoWebp from "../assets/tamari-logo-light.webp";
import logoPng from "../assets/tamari-logo-light.png";

const Footer = () => {
  const { t } = useLanguage();
  const text = useSiteText();
  const currentYear = new Date().getFullYear();
  const email = text("contact.email", "info@tamari.al");
  const phone = text("contact.phone", "+355 69 624 2511");

  return (
    <footer className="bg-[#0A0A0A] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block group">
              <Picture
                webpSrc={logoWebp}
                fallbackSrc={logoPng}
                alt="Tamari"
                className="h-16 lg:h-20 w-auto transition-opacity duration-300 group-hover:opacity-90"
              />
            </Link>
            <p className="text-zinc-400 mt-4 max-w-md">{t.footer.tagline}</p>
            <div className="flex flex-col gap-4 mt-6">
              <div className="flex items-center gap-3 text-zinc-400">
                <Mail className="w-4 h-4" />
                <a href={`mailto:${email}`} className="hover:text-white transition-colors">{email}</a>
              </div>
              <div className="flex items-center gap-3 text-zinc-400" data-testid="footer-phone">
                <Phone className="w-4 h-4" />
                <a href={`tel:${phone.replace(/\s/g, "")}`} className="hover:text-white transition-colors">{phone}</a>
              </div>
              <div className="flex items-start gap-3 text-zinc-400">
                <Factory className="w-4 h-4 mt-1 flex-shrink-0" />
                <div>
                  <span className="text-zinc-500 text-sm">{t.contact.info.slaughterhouse}</span>
                  <p>{text("contact.slaughterhouse_address", t.contact.info.slaughterhouseAddress)}</p>
                  <p className="text-zinc-500 text-sm flex items-center gap-1 mt-1">
                    <Clock className="w-3 h-3" /> {text("contact.slaughterhouse_hours", t.contact.info.slaughterhouseHours)}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 text-zinc-400">
                <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
                <div>
                  <span className="text-zinc-500 text-sm">{t.contact.info.salami}</span>
                  <p>{text("contact.salami_address", t.contact.info.salamiAddress)}</p>
                  <p className="text-zinc-500 text-sm flex items-center gap-1 mt-1">
                    <Clock className="w-3 h-3" /> {text("contact.salami_hours", t.contact.info.salamiHours)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold mb-4">Links</h4>
            <nav className="flex flex-col gap-2">
              <Link to="/services" className="text-zinc-400 hover:text-white transition-colors">
                {t.footer.links.services}
              </Link>
              <Link to="/salami" className="text-zinc-400 hover:text-white transition-colors">
                {t.nav.salami}
              </Link>
              <Link to="/about" className="text-zinc-400 hover:text-white transition-colors">
                {t.footer.links.about}
              </Link>
              <Link to="/contact" className="text-zinc-400 hover:text-white transition-colors">
                {t.footer.links.contact}
              </Link>
            </nav>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold mb-4">{t.nav.services}</h4>
            <nav className="flex flex-col gap-2">
              <Link to="/services" className="text-zinc-400 hover:text-white transition-colors">
                {t.services.import.title}
              </Link>
              <Link to="/services" className="text-zinc-400 hover:text-white transition-colors">
                {t.services.carcass.title}
              </Link>
              <Link to="/services" className="text-zinc-400 hover:text-white transition-colors">
                {t.services.transport.title}
              </Link>
            </nav>
          </div>
        </div>

        <div className="border-t border-zinc-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-zinc-500 text-sm">
            © {currentYear} Tamari. {t.footer.rights}.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
